<script setup>
import { ref, computed } from 'vue';

/**
 * Manage-fields popover: show/hide (checkbox), drag-to-reorder, reset to the
 * default layout. State lives in the shared levellingFieldPrefs composable;
 * this component only renders and emits.
 */
const props = defineProps({
  order: { type: Array, required: true }, // field keys, current order
  hidden: { type: Array, required: true }, // hidden field keys
  defs: { type: Array, required: true }, // field defs (key, label, locked)
  canSetDefault: { type: Boolean, default: false }, // admin: publish org default
});

const emit = defineEmits(['toggle', 'move', 'reset', 'save-default']);

const menuOpen = ref(false);
const dragIndex = ref(null);

const byKey = computed(() => {
  const map = {};
  props.defs.forEach((d) => { map[d.key] = d; });
  return map;
});

const rows = computed(() => props.order.map((k) => byKey.value[k]).filter(Boolean));
const visibleCount = computed(() => rows.value.filter((d) => d.locked || !props.hidden.includes(d.key)).length);

function onDragOver(index, event) {
  event.preventDefault();
  if (dragIndex.value !== null && dragIndex.value !== index) {
    emit('move', dragIndex.value, index);
    dragIndex.value = index;
  }
}
</script>

<template>
  <v-menu v-model="menuOpen" offset-y left :close-on-content-click="false">
    <template v-slot:activator="{ on, attrs }">
      <button class="cc-chipbtn" v-bind="attrs" v-on="on">
        <v-icon size="15">mdi-view-column-outline</v-icon>
        Fields
        <span class="cc-fieldcount">{{ visibleCount }}</span>
      </button>
    </template>
    <v-card class="cc-fieldpanel">
      <div class="cc-fieldpanel-head">
        <div>
          <h4>Manage fields</h4>
          <p>Show, hide and drag to reorder. Saved for next time.</p>
        </div>
        <button class="cc-fieldreset" title="Reset to your organisation's default" @click="emit('reset')">
          <v-icon size="15">mdi-restart</v-icon>
          Reset
        </button>
      </div>
      <div class="cc-fieldpanel-list">
        <div
          v-for="(d, i) in rows"
          :key="d.key"
          class="cc-fieldrow"
          :class="{ 'is-drag': dragIndex === i }"
          @dragover="onDragOver(i, $event)"
        >
          <span
            class="cc-fieldgrip"
            aria-hidden="true"
            draggable="true"
            @dragstart="dragIndex = i"
            @dragend="dragIndex = null"
          >
            <v-icon size="18">mdi-drag-vertical</v-icon>
          </span>
          <label class="cc-fieldlbl">
            <input
              type="checkbox"
              :checked="d.locked || !hidden.includes(d.key)"
              :disabled="d.locked"
              @change="emit('toggle', d.key)"
            />
            <span>{{ d.label }}</span>
          </label>
          <span v-if="d.locked" class="cc-fieldlock" title="Always shown">
            <v-icon size="13">mdi-lock-outline</v-icon>
          </span>
        </div>
      </div>
      <div class="cc-fieldpanel-foot">
        <button v-if="canSetDefault" class="cc-fielddefault" @click="emit('save-default')">
          <v-icon size="15">mdi-pin-outline</v-icon>
          Set as org default
        </button>
        <p class="cc-fieldnote">
          <v-icon size="13">mdi-information-outline</v-icon>
          The organisation default is set by an administrator.
        </p>
      </div>
    </v-card>
  </v-menu>
</template>

<style lang="scss" scoped>
/* Prototype v3-chipbtn — pill, 600/11.5. */
.cc-chipbtn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 11px;
  border: 1px solid var(--cc-border, #e0e0e0);
  border-radius: 999px;
  background: var(--cc-surface, #ffffff);
  font-size: 11.5px;
  font-weight: 600;
  color: var(--cc-text-2, #525c6b);
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: var(--cc-surface-2, #f7f9fb);
    color: var(--cc-text, #1f242d);
  }
}

.cc-fieldcount {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: var(--cc-surface-3, #e2e6ec);
  color: var(--cc-text-2, #444444);
  font-size: 11px;
  font-weight: 700;
}

.cc-fieldpanel {
  width: 300px;
  max-height: 480px;
  display: flex;
  flex-direction: column;
}

.cc-fieldpanel-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  padding: 12px 14px 10px;
  border-bottom: 1px solid var(--cc-border-soft, #eeeeee);

  h4 {
    font-size: 14px;
    font-weight: 700;
    margin: 0;
  }

  p {
    font-size: 12px;
    color: var(--cc-text-3, #888888);
    margin: 2px 0 0;
  }
}

.cc-fieldreset {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--cc-accent, #1976d2);
  flex-shrink: 0;

  &:hover {
    text-decoration: underline;
  }
}

.cc-fieldpanel-list {
  overflow-y: auto;
  padding: 6px 8px;
}

.cc-fieldpanel-foot {
  border-top: 1px solid var(--cc-border-soft, #eeeeee);
  padding: 10px 14px 12px;

  .cc-fielddefault {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    border: 1px solid var(--cc-border, #e0e0e0);
    background: var(--cc-surface, #ffffff);
    border-radius: 7px;
    padding: 6px 10px;
    cursor: pointer;
    font-size: 12.5px;
    font-weight: 600;
    color: var(--cc-text-2, #47505f);

    &:hover {
      border-color: var(--cc-accent, #1d5bd6);
      color: var(--cc-accent, #1d5bd6);
    }
  }

  .cc-fieldnote {
    display: flex;
    align-items: flex-start;
    gap: 5px;
    margin: 8px 0 0;
    font-size: 11.5px;
    line-height: 1.4;
    color: var(--cc-text-3, #8a93a3);

    .v-icon {
      margin-top: 1px;
      flex-shrink: 0;
      color: var(--cc-text-3, #8a93a3) !important;
    }
  }
}

.cc-fieldrow {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 5px 4px;
  border-radius: 6px;

  &.is-drag {
    background: var(--cc-accent-tint, #eef3fc);
  }

  .cc-fieldgrip {
    cursor: grab;
    display: inline-flex;
    color: var(--cc-text-3, #bbbbbb);
  }

  .cc-fieldlbl {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    cursor: pointer;
    font-size: 13.5px;
    color: var(--cc-text, #333333);

    input {
      accent-color: var(--cc-accent, #1d5bd6);
    }

    input:disabled + span {
      color: var(--cc-text-3, #999999);
    }
  }

  .cc-fieldlock {
    color: var(--cc-text-3, #bbbbbb);
    display: inline-flex;
  }
}
</style>
